import React, { useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import { useDebounce } from 'use-debounce';
import { v4 } from 'uuid';
import clsx from 'clsx';
import { EntityContainer } from './EntityContainer';
import { useTemplateAssumedRows, useTemplateConfigs } from '../../hooks/useTemplateList';
import { TableRow, CheckboxInput, Table, TableData, OptionsDropdown } from '@td-client-frontend/common';
import { TrianglebleWarning, Remove } from '@td-client-frontend/icons';
import {
  EntityTransformResponse,
  Template,
  DefaultEntitySchema,
  TemplateDirectiveConfig,
  RawTransformData,
  EntityRow,
  EntityTypes,
  TemplateUpdateMaskConfig,
  TransformFieldValidationError,
  EntityConfigurationData,
  Overrides,
  Disabled
} from '../../@types/Templates';
import { CustomFieldModal } from './CustomFieldModal/CustomFieldModal';
import { EntityTemplateConfiguration } from '../../flows/Reseller/components';
import Modal from '../../components/Modal/NewModal';
import { HeaderDefinition } from '../../@types/TemplateConfigs';
import { useTemplateEntityContext } from '../../context/TemplateEntityContext';

export interface HeaderOption {
  label: string;
  value: TemplateDirectiveConfig[];
  key: string;
}

interface TemplateEntityProps {
  defaults?: DefaultEntitySchema;
  overrides?: Overrides;
  disabled?: Disabled;
  entityType: EntityTypes;
  transformedData: EntityTransformResponse<EntityRow> | RawTransformData<EntityRow>;
  template?: Template;
  onEntityChange: (template: Template) => Promise<void | Error>;
  onDefaultsChange: (name: string, data: EntityConfigurationData) => Promise<void | Error>;
  isLastEntity?: boolean;
}

export function getRowErrors(source: string[] | TransformFieldValidationError[] | undefined, idx: number) {
  const data: Record<string, string> = {};
  source &&
    source.forEach((err: any) => {
      if (err.row === idx) {
        data[err.field] = err.error[0];
      }
    });
  return data;
}

const AssumedRow = ({ onChange, onDelete, rowValues, rowId, headersDefinition }) => {
  const lastHeaderId = headersDefinition?.length - 1;
  return (
    <>
      <tr className="bg-gold" data-testid={`assumedrow-${rowId}`}>
        {headersDefinition?.map((header, headerId) => {
          const isLastField = headerId === lastHeaderId;
          return (
            <td
              className={clsx('text-center p-2', isLastField && 'flex')}
              key={`dataRow-${header.displayName}-${headerId}`}
            >
              <input
                className="flex px-3 py-1 h-10 rounded-md border-1 border-gray-default"
                name={header.objectName}
                onChange={onChange}
                disabled={header.objectName.indexOf('ID') > -1}
                placeholder={header.displayName}
                value={rowValues[header.objectName]}
              />
              {isLastField && (
                <div className="flex pl-3">
                  <Remove className="cursor-pointer" onClick={onDelete} />
                </div>
              )}
            </td>
          );
        })}
      </tr>
    </>
  );
};

export function TemplateEntity({
  defaults,
  overrides,
  disabled,
  entityType,
  transformedData,
  template,
  onEntityChange,
  onDefaultsChange,
  isLastEntity
}: TemplateEntityProps) {
  const [isAssumedRowTouched, setIsAssumedRowTouched] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [customFieldHeader, setCustomFieldHeader] = useState<string>('');
  const [customFieldData, setCustomFieldData] = useState<TemplateDirectiveConfig[]>([]);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState<boolean>(false);
  const toggleCustomFieldModal = () => setShowCustomFieldModal(!showCustomFieldModal);
  const toggleModal = useCallback(() => setShowModal((state) => !state), []);
  const { configs } = useTemplateConfigs(entityType, overrides);
  const templateData: Template = useMemo(() => {
    return JSON.parse(JSON.stringify(template));
  }, [template]); // working copy of the template. Should be used for modification
  const templateAssumedRows =
    templateData && templateData.assumed
      ? (templateData.assumed[entityType] || []).map((rowValues) => ({ rowValues, rowId: v4() }))
      : [];
  const [assumedRows, dispatchAssumedRows] = useTemplateAssumedRows(templateAssumedRows);
  const [debouncedRows] = useDebounce(assumedRows, 750);
  const { setFieldOptions, setFirstDataLine } = useTemplateEntityContext();

  const getDropDownOptions = function (options: string[]): HeaderOption[] {
    if (!options || !Array.isArray(options)) {
      return [];
    }
    const dropDownOptions = options.map(function (name, index) {
      index = index + 1;
      return {
        key: `column-${index}`,
        value: [{ operation: 'column', args: [index] }],
        label: `(${index}) ${template?.config.hasHeaders ? name : ''}`
      };
    });

    return dropDownOptions;
  };
  const ensureEntityTypeInTemplate = (entityType: EntityTypes): void => {
    if (!templateData[entityType]) {
      templateData[entityType] = {};
    }
  };

  const onHeaderClearAction = (header: HeaderDefinition) => {
    ensureEntityTypeInTemplate(entityType);
    templateData[entityType][header.objectName] = [
      {
        operation: 'column',
        args: []
      }
    ];
    onUpdateMaskChange(header, false);
    onEntityChange(templateData);
  };

  const onHeaderDropdownSelected = (headerOption: HeaderOption, header: string) => {
    ensureEntityTypeInTemplate(entityType);
    templateData[entityType][header] = headerOption.value;
    onEntityChange(templateData);
  };

  const onCustomHeaderSelected = (header: string) => {
    setCustomFieldHeader(header);
    setCustomFieldData(templateData[entityType][header]);
    toggleCustomFieldModal();
  };

  const handleCustomSave = (steps: TemplateDirectiveConfig[], header: string) => {
    templateData[entityType][header] = steps;
    onEntityChange(templateData);
  };

  const ensureEntityTypeInUpdateMask = () => {
    if (!templateData.config.updateMask) {
      templateData.config.updateMask = {} as Record<EntityTypes, TemplateUpdateMaskConfig>;
    }

    if (!templateData.config.updateMask[entityType]) {
      templateData.config.updateMask[entityType] = {};
    }
  };

  const onUpdateMaskChange = (header: HeaderDefinition, updateMask: boolean) => {
    ensureEntityTypeInUpdateMask();
    // @ts-ignore
    templateData.config.updateMask[entityType][header.objectName] = updateMask;

    onEntityChange(templateData);
  };

  const getUpdateMask = (header: HeaderDefinition) => {
    if (header.updateDisabled) {
      return false;
    }

    if (templateData.config?.updateMask) {
      ensureEntityTypeInUpdateMask();
      return templateData.config.updateMask[entityType]?.[header.objectName];
    }

    return header.canUpdate;
  };

  const dropDownOptions: HeaderOption[] = getDropDownOptions(transformedData.firstLine);

  useEffect(() => {
    setFieldOptions(dropDownOptions);
    setFirstDataLine(transformedData.firstDataLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFirstDataLine, setFieldOptions, transformedData.firstLine, transformedData.firstDataLine]);

  function getEntityDropdownOptions(header: HeaderDefinition) {
    return [
      ...dropDownOptions.map((column) => {
        return {
          label: column.label,
          onClick: () => {
            onHeaderDropdownSelected(
              {
                label: column.label,
                value: column.value,
                key: column.key
              },
              header.objectName
            );
          }
        };
      }),
      {
        label: 'Custom',
        onClick: () => onCustomHeaderSelected(header.objectName)
      },
      {
        label: 'Clear',
        onClick: () => onHeaderClearAction(header)
      }
    ];
  }

  function getEntityDropdownLabel(header: HeaderDefinition) {
    const columnConfig: TemplateDirectiveConfig[] = templateData[entityType]?.[header.objectName];

    if (columnConfig?.length === 1 && columnConfig[0].operation === 'column' && columnConfig[0].args[0]) {
      const index = columnConfig[0].args[0] - 1;
      return dropDownOptions[index] ? dropDownOptions[index].label : 'Click To Set';
    } else if (columnConfig?.length && columnConfig[0]?.operation) {
      return columnConfig[0].args.length ? 'Custom' : 'Click To Set';
    }
    return 'Click To Set';
  }

  const newLineHeaders = configs?.headers;
  const isRowAssumed = configs?.settings?.assumedRows;
  const initialAssumedRowValues = newLineHeaders?.reduce((acc, value) => ({ ...acc, [value.objectName]: '' }), {});

  const onAddRowClick = () => {
    dispatchAssumedRows({ type: 'add', payload: AssumedRow, rowId: v4(), rowValues: initialAssumedRowValues });
    if (!isAssumedRowTouched) setIsAssumedRowTouched(true);
  };

  const handleRowDelete = (rowId: string) => {
    dispatchAssumedRows({ type: 'remove', rowId });
    if (!isAssumedRowTouched) setIsAssumedRowTouched(true);
  };

  const handleRowInputChange = (rowId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatchAssumedRows({ type: 'updateValue', rowId, objectName: name, value });
    if (!isAssumedRowTouched) setIsAssumedRowTouched(true);
  };

  const handleEntityRefresh = useCallback(() => {
    const assumedRowValues = assumedRows.map((row: any) => row.rowValues);
    const newEntityState = {
      ...templateData,
      assumed: { ...(templateData.assumed || {}), [entityType]: [...assumedRowValues] }
    } as Template;
    onEntityChange(newEntityState);
  }, [assumedRows, entityType, onEntityChange, templateData]);

  useEffect(() => {
    if (isAssumedRowTouched) handleEntityRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRows]);

  useEffect(() => {
    if (isLastEntity) handleEntityRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastEntity]);

  const assumedRowsLines =
    assumedRows &&
    assumedRows.map(({ rowValues, rowId }) => (
      <AssumedRow
        onDelete={() => handleRowDelete(rowId)}
        onChange={handleRowInputChange(rowId)}
        key={rowId}
        rowValues={rowValues}
        headersDefinition={newLineHeaders}
      />
    ));

  return (
    <Fragment>
      <EntityContainer
        label={configs?.displayName}
        showInfoModal={toggleModal}
        defaultConfig={defaults}
        onAddRowClick={isRowAssumed ? onAddRowClick : undefined}
      >
        {showCustomFieldModal && (
          <CustomFieldModal
            data={customFieldData ?? []}
            header={customFieldHeader}
            isOpen={showCustomFieldModal}
            toggleCustomFieldModal={toggleCustomFieldModal}
            onSave={handleCustomSave}
            dropDownOptions={dropDownOptions}
            firstDataLine={transformedData.firstDataLine}
          />
        )}
        <Table styles="flex flex-col sm:flex-row sm:flex-wrap justify-between border border-t-0 right-20 w-full overflow-x-auto">
          <thead>
            {configs?.headers && (
              <>
                <tr className="border-b">
                  {configs.headers.map((header, idx) => (
                    <th
                      key={`title-${header.displayName}-${idx}`}
                      className="min-w-12 px-5 pt-5 pb-2 text-lg font-bold text-left align-bottom"
                    >
                      {header.displayName}
                    </th>
                  ))}
                </tr>
                <tr className="bg-gray-15 mx-5 py-2 border-b">
                  {configs.headers.map((header, idx) => (
                    <th key={`select-${header.displayName}-${idx}`} className="min-w-12 px-2 py-2 bg-grey-80">
                      <OptionsDropdown
                        options={getEntityDropdownOptions(header)}
                        label={getEntityDropdownLabel(header)}
                        styles="justify-between w-full border rounded-lg font-normal"
                        dropdownStyles="h-40 overflow-auto"
                      />
                    </th>
                  ))}
                </tr>
                <tr className="py-2 border-b">
                  {configs.headers.map((header, idx) => (
                    <th key={`checkbox-${header.displayName}-${idx}`} className="min-w-12">
                      <div className="flex justify-left px-5 py-2">
                        <CheckboxInput
                          id={header.displayName}
                          checked={getUpdateMask(header)}
                          disabled={header.updateDisabled}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onUpdateMaskChange(header, e.target.checked)
                          }
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </>
            )}
          </thead>
          <tbody>
            {assumedRowsLines}
            {(transformedData.rows || []).map((dataRow, rowIdx) => {
              const rowErrors = getRowErrors(transformedData.errors, rowIdx + 1);
              return (
                <TableRow key={`dataRow-${rowIdx}`}>
                  {configs?.headers?.map((header, idx) => {
                    return (
                      <TableData key={`dataRow-${header.displayName}-${idx}`}>
                        <p>{dataRow[header.objectName]}</p>
                        {rowErrors[header.objectName] && (
                          <p className="text-danger flex flex-nowrap gap-2 items-center">
                            <TrianglebleWarning fill="#ff0000" />
                            {rowErrors[header.objectName]}
                          </p>
                        )}
                      </TableData>
                    );
                  })}
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </EntityContainer>
      {/* Modal */}
      {defaults && (
        <Modal
          isOpen={showModal}
          onClose={toggleModal}
          header={`${entityType[0].toUpperCase() + entityType.slice(1)} Defaults`}
          size="medium"
        >
          <EntityTemplateConfiguration
            entityName={entityType}
            schema={defaults}
            overrides={overrides}
            disabled={disabled}
            onSave={onDefaultsChange}
            hideModal={toggleModal}
          />
        </Modal>
      )}
    </Fragment>
  );
}

export default TemplateEntity;
